/**
 * AI Model Management Settings
 * =============================
 * Assign specific OpenRouter models to each platform feature.
 * Sync latest available models, test model responses, manage API key.
 *
 * Features:
 *  - OpenRouter API key management
 *  - Per-task model assignment (primary + fallback)
 *  - Live model sync from OpenRouter catalogue
 *  - One-click test per task
 *  - Enable/disable individual AI tasks
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { aiTasksApi, API_BASE } from '../../lib/api';
import type { TaskConfig, OpenRouterModel } from '../../types/titan';

// ── Task groupings for display ────────────────────────────────────────────────

const TASK_GROUPS: { label: string; icon: string; tasks: string[] }[] = [
  {
    label: 'Trading Core',
    icon: '⚡',
    tasks: [
      'SIGNAL_VALIDATION',
      'RISK_EVALUATION',
      'RISK_MONITORING',
      'TRADE_MONITOR_INSIGHT',
    ],
  },
  {
    label: 'War Room & Strategy',
    icon: '🎯',
    tasks: [
      'DAILY_WAR_ROOM',
      'STRATEGY_TRANSLATION',
      'STRATEGY_REFINEMENT',
    ],
  },
  {
    label: 'Market Intelligence',
    icon: '📡',
    tasks: [
      'NEWS_SCORING',
      'NEWS_INTELLIGENCE',
      'CATALYST_DETECTION',
      'SENTIMENT_ANALYSIS',
      'FDA_NEWS_ANALYSIS',
      'BIOTECH_ANALYSIS',
    ],
  },
  {
    label: 'Hermes Agent',
    icon: '🤖',
    tasks: [
      'HERMES_STRATEGY_OPTIMIZATION',
      'HERMES_PATTERN_DISCOVERY',
    ],
  },
  {
    label: 'General',
    icon: '💬',
    tasks: [
      'GENERAL_CHAT',
      'SESSION_SUMMARY',
      'POST_DAY_WRAP_UP',
    ],
  },
];

// ── Model selector component ──────────────────────────────────────────────────

const ModelSelect: React.FC<{
  value: string;
  models: OpenRouterModel[];
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ value, models, onChange, placeholder = 'Select model…' }) => {
  const free    = models.filter(m => m.tier === 'free');
  const premium = models.filter(m => m.tier !== 'free');

  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full rounded border border-border bg-background px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
    >
      <option value="">{placeholder}</option>
      {free.length > 0 && (
        <optgroup label="── Free models ──">
          {free.map(m => (
            <option key={m.id} value={m.id}>
              {m.name || m.id}
            </option>
          ))}
        </optgroup>
      )}
      {premium.length > 0 && (
        <optgroup label="── Premium models ──">
          {premium.map(m => (
            <option key={m.id} value={m.id}>
              {m.name || m.id}
              {m.cost_in ? ` ($${(m.cost_in * 1000).toFixed(3)}/1k in)` : ''}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
};

// ── Task row component ────────────────────────────────────────────────────────

const TaskRow: React.FC<{
  taskId: string;
  config: TaskConfig;
  models: OpenRouterModel[];
  onChange: (taskId: string, field: keyof TaskConfig, value: unknown) => void;
  onTest: (taskId: string) => void;
  testing: boolean;
  testResult: string | null;
}> = ({ taskId, config, models, onChange, onTest, testing, testResult }) => (
  <div className={`rounded-lg border p-3 transition-colors ${config.enabled ? 'border-border bg-card' : 'border-border/40 bg-muted/20 opacity-60'}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-foreground">{config.display_name || taskId}</span>
          {!config.enabled && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">disabled</span>
          )}
        </div>
        {config.description && (
          <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug">{config.description}</p>
        )}
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div>
            <label className="mb-0.5 block text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Primary model</label>
            <ModelSelect
              value={config.primary_model || ''}
              models={models}
              onChange={v => onChange(taskId, 'primary_model', v)}
              placeholder="Choose primary…"
            />
          </div>
          <div>
            <label className="mb-0.5 block text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Fallback model</label>
            <ModelSelect
              value={config.fallback_model || ''}
              models={models}
              onChange={v => onChange(taskId, 'fallback_model', v)}
              placeholder="Choose fallback…"
            />
          </div>
        </div>
        {testResult && (
          <div className={`mt-2 rounded p-2 text-[11px] ${testResult.startsWith('✅') ? 'bg-emerald-500/10 text-emerald-600' : 'bg-rose-500/10 text-rose-600'}`}>
            {testResult}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-2 shrink-0">
        {/* Enable toggle */}
        <button
          onClick={() => onChange(taskId, 'enabled', !config.enabled)}
          title={config.enabled ? 'Disable task' : 'Enable task'}
          className={`relative h-5 w-9 rounded-full transition-colors focus:outline-none ${config.enabled ? 'bg-primary' : 'bg-muted'}`}
        >
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${config.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </button>
        {/* Test button */}
        <button
          onClick={() => onTest(taskId)}
          disabled={testing || !config.enabled}
          className="rounded border border-border px-2 py-1 text-[10px] text-muted-foreground hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
        >
          {testing ? '…' : 'Test'}
        </button>
      </div>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const AIModelSettings: React.FC = () => {
  const [tasks, setTasks]           = useState<Record<string, TaskConfig>>({});
  const [models, setModels]         = useState<OpenRouterModel[]>([]);
  const [apiKey, setApiKey]         = useState('');
  const [keyMasked, setKeyMasked]   = useState('');
  const [keyConfigured, setKeyConfigured] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [syncing, setSyncing]       = useState(false);
  const [savedAt, setSavedAt]       = useState<string | null>(null);
  const [syncedAt, setSyncedAt]     = useState<string | null>(null);
  const [modelCount, setModelCount] = useState(0);
  const [testing, setTesting]       = useState<Record<string, boolean>>({});
  const [testResults, setTestResults] = useState<Record<string, string | null>>({});
  const [error, setError]           = useState<string | null>(null);
  const [dirty, setDirty]           = useState(false);
  const pendingRef                  = useRef<Record<string, Partial<TaskConfig>>>({});

  // ── Load ──────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [taskRes, modelRes, keyRes] = await Promise.all([
        aiTasksApi.getTasks(),
        aiTasksApi.getModels().catch(() => null),
        aiTasksApi.listKeys().catch(() => null),
      ]);
      if (taskRes?.success) setTasks(taskRes.tasks);
      if (modelRes?.synced && modelRes.openrouter) {
        setModels(modelRes.openrouter);
        setModelCount(modelRes.model_count ?? 0);
        setSyncedAt(modelRes.synced_at ?? null);
      }
      const orKey = keyRes?.keys?.find(k => k.provider_id === 'openrouter');
      if (orKey) {
        setKeyConfigured(orKey.configured);
        setKeyMasked(orKey.configured ? '••••••••••••••••' : '');
      }
    } catch (e) {
      setError('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleTaskChange = useCallback(
    (taskId: string, field: keyof TaskConfig, value: unknown) => {
      setTasks(prev => ({ ...prev, [taskId]: { ...prev[taskId], [field]: value } }));
      pendingRef.current[taskId] = { ...pendingRef.current[taskId], [field]: value as any };
      setDirty(true);
      setSavedAt(null);
    },
    [],
  );

  const handleSave = async () => {
    if (!dirty || Object.keys(pendingRef.current).length === 0) return;
    setSaving(true); setError(null);
    try {
      await aiTasksApi.batchUpdate(pendingRef.current);
      pendingRef.current = {};
      setDirty(false);
      setSavedAt(new Date().toLocaleTimeString());
    } catch {
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true); setError(null);
    try {
      await aiTasksApi.syncModels();
      const res = await aiTasksApi.getModels();
      if (res?.synced && res.openrouter) {
        setModels(res.openrouter);
        setModelCount(res.model_count ?? 0);
        setSyncedAt(new Date().toLocaleTimeString());
      }
    } catch {
      setError('Model sync failed — check OpenRouter key');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveKey = async () => {
    if (!apiKey.trim()) return;
    setSaving(true); setError(null);
    try {
      await aiTasksApi.setKey('openrouter', { api_key: apiKey.trim() });
      setKeyConfigured(true);
      setKeyMasked('••••••••••••••••');
      setApiKey('');
    } catch {
      setError('Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKey = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('Remove the OpenRouter API key? All AI features will stop working.')) return;
    setSaving(true);
    try {
      await aiTasksApi.deleteKey('openrouter');
      setKeyConfigured(false); setKeyMasked('');
    } catch {
      setError('Failed to remove key');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (taskId: string) => {
    setTesting(p => ({ ...p, [taskId]: true }));
    setTestResults(p => ({ ...p, [taskId]: null }));
    try {
      // POST a minimal test via the chat endpoint, labelled with the task
      const res = await fetch(`${API_BASE}/api/intelligence/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getIdToken()}` },
        body: JSON.stringify({ message: `[TASK_TEST:${taskId}] Reply with one sentence confirming you received this test.` }),
      });
      const data = await res.json();
      const reply = data?.reply || '';
      // Detect backend error messages (empty response, credits, invalid key, unavailable)
      const errorSignals = [
        'unavailable', 'insufficient', 'invalid', 'not found', 'error',
        'empty', 'credits', 'api key', 'try again', 'failed',
      ];
      const isBackendError = !reply || errorSignals.some(s => reply.toLowerCase().includes(s));
      if (!isBackendError) {
        setTestResults(p => ({ ...p, [taskId]: `✅ ${reply.slice(0, 120)}` }));
      } else {
        setTestResults(p => ({ ...p, [taskId]: `❌ ${reply.slice(0, 150)}` }));
      }
    } catch {
      setTestResults(p => ({ ...p, [taskId]: '❌ Request failed — check console' }));
    } finally {
      setTesting(p => ({ ...p, [taskId]: false }));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="ml-3 text-sm text-muted-foreground">Loading AI settings…</span>
      </div>
    );
  }

  const tasksByGroup = TASK_GROUPS.map(group => ({
    ...group,
    configs: group.tasks
      .filter(t => tasks[t])
      .map(t => ({ taskId: t, config: tasks[t] })),
  })).filter(g => g.configs.length > 0);

  const ungrouped = Object.entries(tasks).filter(
    ([id]) => !TASK_GROUPS.flatMap(g => g.tasks).includes(id)
  );

  return (
    <div className="space-y-6 p-1">

      {/* ── Header + Save ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">AI Model Management</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Assign OpenRouter models to each platform feature. Changes apply immediately after Save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {savedAt && <span className="text-[11px] text-emerald-500">Saved {savedAt}</span>}
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-600">
          {error}
        </div>
      )}

      {/* ── API Key ────────────────────────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-foreground">OpenRouter API Key</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              All AI features route through OpenRouter.{' '}
              <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noreferrer"
                className="text-primary underline underline-offset-2">Get a key →</a>
            </p>
          </div>
          <div className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${keyConfigured ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${keyConfigured ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {keyConfigured ? 'Configured' : 'Not set'}
          </div>
        </div>

        <div className="flex gap-2">
          <input
            type="password"
            value={apiKey || keyMasked}
            onChange={e => setApiKey(e.target.value)}
            onFocus={() => { if (keyConfigured && !apiKey) setKeyMasked(''); }}
            placeholder={keyConfigured ? 'Enter new key to replace current' : 'sk-or-v1-…'}
            className="flex-1 rounded border border-border bg-background px-3 py-1.5 text-xs font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            onClick={handleSaveKey}
            disabled={!apiKey.trim() || saving}
            className="rounded border border-primary px-3 py-1.5 text-xs text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-40 transition-colors"
          >
            Save key
          </button>
          {keyConfigured && (
            <button
              onClick={handleDeleteKey}
              className="rounded border border-rose-500/40 px-3 py-1.5 text-xs text-rose-500 hover:bg-rose-500/10 transition-colors"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {/* ── Model Catalogue ────────────────────────────────────────────── */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground">Model Catalogue</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {modelCount > 0
                ? `${modelCount} models available${syncedAt ? ` — synced ${syncedAt}` : ''}`
                : 'Sync to load available models from OpenRouter'}
            </p>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing || !keyConfigured}
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs hover:border-primary hover:text-primary disabled:opacity-40 transition-colors"
          >
            <span className={syncing ? 'animate-spin' : ''}>⟳</span>
            {syncing ? 'Syncing…' : 'Sync models'}
          </button>
        </div>
        {models.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
            <div className="rounded bg-muted/50 px-2 py-1.5">
              <div className="font-semibold text-foreground">{models.length}</div>
              <div className="text-muted-foreground">Total models</div>
            </div>
            <div className="rounded bg-muted/50 px-2 py-1.5">
              <div className="font-semibold text-emerald-600">{models.filter(m => m.tier === 'free').length}</div>
              <div className="text-muted-foreground">Free tier</div>
            </div>
            <div className="rounded bg-muted/50 px-2 py-1.5">
              <div className="font-semibold text-amber-600">{models.filter(m => m.tier !== 'free').length}</div>
              <div className="text-muted-foreground">Premium</div>
            </div>
          </div>
        )}
      </div>

      {/* ── Task Groups ────────────────────────────────────────────────── */}
      {models.length === 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-xs text-amber-600">
          ⚠ Sync the model catalogue above to enable model selection dropdowns.
          You can still enable/disable tasks and save without syncing.
        </div>
      )}

      {tasksByGroup.map(group => (
        <div key={group.label}>
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            <span>{group.icon}</span> {group.label}
          </h3>
          <div className="space-y-2">
            {group.configs.map(({ taskId, config }) => (
              <TaskRow
                key={taskId}
                taskId={taskId}
                config={config}
                models={models}
                onChange={handleTaskChange}
                onTest={handleTest}
                testing={!!testing[taskId]}
                testResult={testResults[taskId] ?? null}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Ungrouped tasks */}
      {ungrouped.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Other</h3>
          <div className="space-y-2">
            {ungrouped.map(([taskId, config]) => (
              <TaskRow
                key={taskId}
                taskId={taskId}
                config={config}
                models={models}
                onChange={handleTaskChange}
                onTest={handleTest}
                testing={!!testing[taskId]}
                testResult={testResults[taskId] ?? null}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Footer Save ────────────────────────────────────────────────── */}
      {dirty && (
        <div className="sticky bottom-0 flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 px-4 py-3">
          <span className="text-xs text-primary font-medium">You have unsaved changes</span>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
          >
            {saving ? 'Saving…' : 'Save all changes'}
          </button>
        </div>
      )}
    </div>
  );
};

// Helper — get Firebase ID token for test request
async function getIdToken(): Promise<string> {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  return user ? user.getIdToken() : '';
}

export default AIModelSettings;
